import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/ui/Header';
import ScanControlPanel from './components/ScanControlPanel';
import VehicleDataTable from './components/VehicleDataTable';
import AlertNotification from './components/AlertNotification';
import SessionStats from './components/SessionStats';
import { vehicleApi, transformVehiclesData } from '../../utils/vehicleApi';

const VehicleMonitoringDashboard = () => {
  // State management
  const [isScanning, setIsScanning] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionTimer, setSessionTimer] = useState(0);
  const [apiStatus, setApiStatus] = useState('connecting');
  const [lastScanTime, setLastScanTime] = useState(null);
  const [alert, setAlert] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [sessionStats, setSessionStats] = useState({
    totalDetected: 0,
    totalScans: 0,
    activeTime: '00:00:00',
    lastUpdate: null
  });

  // Mark vehicles as read after detection
  const markVehiclesAsRead = useCallback(async (vehicles) => {
    if (!vehicles || vehicles?.length === 0) return;

    try {
      const documentIds = vehicles
        ?.map(vehicle => vehicle?.documentId)
        ?.filter(Boolean);

      if (documentIds?.length > 0) {
        const updateResult = await vehicleApi?.markVehiclesAsRead(documentIds);
        
        if (updateResult?.success) {
          console.log(`Marcados como leídos: ${updateResult?.updated?.length} vehículos`);
          
          if (updateResult?.failed?.length > 0) {
            console.warn(`Fallos en actualización: ${updateResult?.failed?.length} vehículos`, updateResult?.failed);
          }
        } else {
          console.error('Error al marcar vehículos como leídos:', updateResult?.error);
        }
      }
    } catch (error) {
      console.error('Error en proceso de marcado como leído:', error);
    }
  }, []);

  // Format timer display
  const formatTimer = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours?.toString()?.padStart(2, '0')}:${minutes?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  // Fetch vehicles from Strapi API
  const fetchVehiclesFromAPI = useCallback(async (page = 1, pageSize = 25) => {
    try {
      setLoading(true);
      setLastScanTime(new Date()?.toISOString());

      const response = await vehicleApi?.fetchVehicles({
        page,
        pageSize,
        flagFilter: true,
        sortBy: 'id:asc'
      });

      if (response?.success) {
        const transformedVehicles = transformVehiclesData(response?.data);
        
        // Update vehicles state based on scanning mode
        if (isScanning) {
          // In scanning mode, check for new vehicles
          setVehicles(prevVehicles => {
            const existingIds = prevVehicles?.map(v => v?.id) || [];
            const newVehicles = transformedVehicles?.filter(v => !existingIds?.includes(v?.id));
            
            if (newVehicles?.length > 0) {
              // Immediately mark detected vehicles as read
              markVehiclesAsRead(newVehicles);
              
              // Enhanced alert for new vehicles detected with improved styling
              setAlert({
                type: 'success',
                title: '🎯 Nuevos Vehículos Detectados',
                message: `Se detectaron ${newVehicles?.length} nuevos vehículo(s) y fueron marcados como leídos automáticamente. Sistema funcionando correctamente.`,
                timestamp: new Date()?.toISOString()
              });
              
              // Update session stats
              setSessionStats(prev => ({
                ...prev,
                totalDetected: prev?.totalDetected + newVehicles?.length,
                lastUpdate: new Date()?.toISOString()
              }));
              
              return [...newVehicles, ...prevVehicles];
            }
            
            return prevVehicles;
          });
        } else {
          // In regular mode, replace all vehicles
          setVehicles(transformedVehicles);
        }

        // Update pagination info
        setTotalVehicles(response?.meta?.pagination?.total || 0);
        setCurrentPage(response?.meta?.pagination?.page || 1);
        
        setApiStatus('connected');
        
        // Update scan statistics
        setSessionStats(prev => ({
          ...prev,
          totalScans: prev?.totalScans + 1,
          lastUpdate: new Date()?.toISOString()
        }));

      } else {
        throw new Error(response?.error?.message || 'Error al obtener vehículos');
      }

    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setApiStatus('error');
      setAlert({
        type: 'error',
        title: 'Error de Conexión',
        message: error?.message || 'No se pudo conectar con el SDK del Reader',
        timestamp: new Date()?.toISOString()
      });
    } finally {
      setLoading(false);
    }
  }, [isScanning, markVehiclesAsRead]);

  // Perform scan - now uses real API
  const performScan = useCallback(async () => {
    if (!isScanning) return;
    await fetchVehiclesFromAPI(1, 50); // Fetch more vehicles during scanning
  }, [isScanning, fetchVehiclesFromAPI]);

  // Initial load of vehicles
  useEffect(() => {
    fetchVehiclesFromAPI(1, 25);
  }, []);

  // Toggle scanning state
  const handleToggleScan = () => {
    setIsScanning(prev => {
      const newState = !prev;
      
      if (newState) {
        setAlert({
          type: 'success',
          title: 'Escaneo Iniciado',
          message: 'Monitoreando Reader en busca de nuevos vehículos',
          timestamp: new Date()?.toISOString()
        });
        // Clear vehicles when starting scan to show only new detections
        setVehicles([]);
        setSessionStats(prev => ({
          ...prev,
          totalDetected: 0,
          totalScans: 0
        }));
      } else {
        setAlert({
          type: 'warning',
          title: 'Escaneo Detenido',
          message: 'El monitoreo automático se ha pausado',
          timestamp: new Date()?.toISOString()
        });
        // Reload all vehicles when stopping scan
        fetchVehiclesFromAPI(1, 25);
      }
      
      return newState;
    });
  };

  // Dismiss alert
  const handleDismissAlert = () => {
    setAlert(null);
  };

  // Manual refresh
  const handleManualRefresh = () => {
    if (!isScanning) {
      fetchVehiclesFromAPI(currentPage, 25);
    }
  };

  // Session timer effect
  useEffect(() => {
    let interval;
    if (isScanning) {
      interval = setInterval(() => {
        setSessionTimer(prev => {
          const newTime = prev + 1;
          setSessionStats(prevStats => ({
            ...prevStats,
            activeTime: formatTimer(newTime)
          }));
          return newTime;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isScanning]);

  // Scanning interval effect - polls API every 3 seconds
  useEffect(() => {
    let scanInterval;
    if (isScanning) {
      // Initial scan
      performScan();
      // Set up 3-second interval for API polling
      scanInterval = setInterval(performScan, 3000);
    }
    return () => clearInterval(scanInterval);
  }, [isScanning, performScan]);

  // Auto-dismiss alerts after 5 seconds
  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => {
        setAlert(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Panel de Monitoreo de Vehículos
            </h1>
            <p className="text-muted-foreground">
              Sistema integrado con READER RFID para monitoreo de vehículos en tiempo real
            </p>
            {totalVehicles > 0 && (
              <p className="text-sm text-muted-foreground mt-1">
                Total de vehículos registrados: {totalVehicles}
              </p>
            )}
          </div>

          {/* Alert Notification */}
          {alert && (
            <div className="mb-6">
              <AlertNotification 
                alert={alert} 
                onDismiss={handleDismissAlert} 
              />
            </div>
          )}

          {/* Control Panel */}
          <div className="mb-8">
            <ScanControlPanel
              isScanning={isScanning}
              onToggleScan={handleToggleScan}
              sessionTimer={sessionTimer}
              apiStatus={apiStatus}
              lastScanTime={lastScanTime}
              onManualRefresh={handleManualRefresh}
            />
          </div>

          {/* Session Statistics */}
          <div className="mb-8">
            <SessionStats 
              stats={sessionStats} 
              totalVehicles={totalVehicles}
              isScanning={isScanning}
            />
          </div>

          {/* Vehicle Data Table */}
          <div className="mb-8">
            <VehicleDataTable
              vehicles={vehicles}
              loading={loading}
              error={apiStatus === 'error' ? 'Error de conexión con el SDK del Reader' : null}
              isScanning={isScanning}
              totalVehicles={totalVehicles}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default VehicleMonitoringDashboard;